import { useLocation, useParams, Link } from 'react-router-dom'
import { ArrowLeft, Layers, MapPin, Phone, Pill } from 'lucide-react'
import BSDCard from '../BSDCard'
import DistrictMap from '../DistrictMap'
import { AMBITI_STATS } from '../../data/ambitiStats'
import { findFarmaciaBySlug } from '../../data/farmacie'

const FARMACIA_SHORT_LABELS: Record<string, string> = {
  'Prenotazione di visite specialistiche ed esami di laboratorio (CUP)': 'CUP',
  'Servizio di scelta/revoca medico di medicina generale': 'Scelta/revoca medico',
  'Servizio Fascicolo Sanitario Elettronico (attivazione, arricchimento, consultazione)': 'Fascicolo Sanitario',
  'APP NOTIFY (registrazione in farmacia del numero di telefono dei cittadini destinato alla ricezione dei codici OTP e NRE)': 'APP NOTIFY',
  'Partecipazione campagna antinfluenzale (vaccinazione antinfluenzale)': 'Vaccino antinfluenzale',
  'Partecipazione campagna anti Covid-19 (vaccinazione anti SARS-COV2)': 'Vaccino Covid-19',
  'Allestimento terapie personalizzate (deblistering)': 'Deblistering',
  'Misurazione della capacità polmonare tramite auto-spirometria': 'Auto-spirometria',
  'Servizio di consegna a domicilio di farmaci in orario diurno e festivo': 'Consegna domicilio',
  'Consulenza nutrizionale (nutrizionista)': 'Nutrizionista',
  'Consulenza psicologica (psicologo)': 'Psicologo',
  'Presenza di dispositivi semiautomatici per la defibrillazione (DAE)': 'DAE',
  'Infermiere': 'Infermiere',
  'Fisioterapista': 'Fisioterapista',
  'Altri operatori sanitari (esclusi medici, veterinari e odontoiatri)': 'Altri operatori sanitari',
  'Misurazione della pressione arteriosa': 'Pressione arteriosa',
  "Misurazione non invasiva della saturazione percentuale dell'ossigeno": 'Saturazione ossigeno',
  'Screening colon-retto per la rilevazione di sangue occulto nelle feci': 'Screening colon-retto',
  'Elettrocardiogramma (ECG)': 'ECG',
  'Holter pressorio': 'Holter pressorio',
  'Holter cardiaco': 'Holter cardiaco',
  'Test per glicemia': 'Test glicemia',
  'Test per profilo lipidico, colesterolo totale, HDL, LDL e trigliceridi': 'Test colesterolo/lipidi',
  'Test per misurazione emoglobina,creatinina, transaminasi, ematocrito, emoglobina glicata': 'Test emoglobina',
  'Test per la misurazione di componenti delle urine (acido ascorbico, chetoni, urobilinogeno, bilirubina, leucociti, nitriti, PH, sangue, proteine)': 'Test urine',
  'Test ovulazione': 'Test ovulazione',
  'Test di gravidanza': 'Test gravidanza',
  "Test per menopausa per la misurazione dell'ormone FSH nelle urine": 'Test menopausa',
  'test Helicobacter pylori': 'Test H. pylori',
  'Test vitamina D': 'Test vitamina D',
  'test Proteina C reattiva': 'Test proteina C',
  'Tampone PCR Molecolare Covid-19': 'Tampone PCR Covid',
  'Tampone antigenico Covid-19': 'Tampone antigenico Covid',
  'Test sierologico Covid-19': 'Test sierologico Covid',
  'Tampone faringeo streptococco': 'Tampone streptococco',
  'Altri eventuali Servizi Offerti': 'Altri servizi',
}

function fallback(value?: string) {
  return value && value.trim() ? value : '—'
}

export default function FarmaciaDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const state = location.state as { from?: string } | null
  const backTo = state?.from === '/farmacie' ? '/farmacie' : '/farmacie'

  const farmacia = slug ? findFarmaciaBySlug(slug) : undefined

  if (!farmacia) {
    return (
      <div className="cdc-detail-page">
        <div className="cdc-detail-container">
          <Link to={backTo} className="cdc-detail-back"><ArrowLeft size={16} /> Torna</Link>
          <h1>Farmacia non trovata</h1>
        </div>
      </div>
    )
  }

  const ambito = AMBITI_STATS.find(a => a.id === farmacia.ambitoId)
  const color = '#059669'

  const serviziAttivi = Object.entries(farmacia.servizi)
    .filter(([k, v]) => k !== 'Altri eventuali Servizi Offerti' && v && v.trim() !== '')
  const serviziNonAttivi = Object.entries(farmacia.servizi)
    .filter(([k, v]) => k !== 'Altri eventuali Servizi Offerti' && (!v || v.trim() === ''))
  const altriServizi = farmacia.servizi['Altri eventuali Servizi Offerti']

  return (
    <div className="cdc-detail-page">
      <div className="cdc-detail-container">
        <Link to={backTo} className="cdc-detail-back"><ArrowLeft size={16} /> Torna alle farmacie</Link>

        <div className="cdc-detail-header">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999,
              color, background: `${color}20`, border: `1px solid ${color}40`,
            }}>
              <Pill size={12} /> Farmacia dei Servizi
            </span>
            {ambito && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
                color: 'var(--accent-blue)', background: 'hsla(210,100%,56%,0.08)', border: '1px solid hsla(210,100%,56%,0.2)',
              }}>
                <Layers size={12} /> Ambito di {ambito.nome}
              </span>
            )}
          </div>

          <h1 className="cdc-detail-title">{farmacia.ragioneSociale}</h1>
        </div>

        <div className="cdc-detail-row">
          <BSDCard kind="custom" className="cdc-detail-info-card" sezione="Farmacie">
            <h2 className="cdc-detail-section-title"><MapPin size={16} /> Informazioni</h2>
            <dl className="cdc-detail-dl">
              <div><dt>Codice</dt><dd>{fallback(farmacia.codice)}</dd></div>
              <div><dt>Comune</dt><dd>{fallback(farmacia.comune.charAt(0).toUpperCase() + farmacia.comune.slice(1).toLowerCase())}</dd></div>
              {farmacia.frazione && <div><dt>Frazione</dt><dd>{farmacia.frazione}</dd></div>}
              <div><dt>Provincia</dt><dd>{fallback(farmacia.provincia)}</dd></div>
              <div><dt>Indirizzo</dt><dd><MapPin size={13} /> {fallback(farmacia.indirizzo)}</dd></div>
              {farmacia.telefono && <div><dt>Telefono</dt><dd><Phone size={13} /> {farmacia.telefono}</dd></div>}
            </dl>
          </BSDCard>

          {farmacia.ambitoId && (
            <BSDCard kind="custom" className="cdc-detail-map-card" sezione="Farmacie">
              <h2 className="cdc-detail-section-title"><Layers size={16} /> Ambito territoriale</h2>
              <DistrictMap
                onSelectDistrict={() => {}}
                onClearSelection={() => {}}
                selectedDistrictId={farmacia.ambitoId}
                allowDistrictSelection={false}
                showLegend={false}
                showInstructionText={false}
              />
            </BSDCard>
          )}
        </div>

        <BSDCard kind="custom" className="cdc-detail-services-card" sezione="Farmacie">
          <h2 className="cdc-detail-section-title"><Pill size={16} /> Servizi offerti ({serviziAttivi.length})</h2>
          <div className="cdc-detail-sections">
            <div className="cdc-detail-sezione">
              <div className="cdc-detail-servizi-grid">
                {serviziAttivi.map(([key]) => (
                  <div key={key} className="cdc-detail-servizio" style={{ borderLeft: `3px solid ${color}` }}>
                    <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>
                      {FARMACIA_SHORT_LABELS[key] ?? key}
                    </p>
                    <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--text-3)', lineHeight: 1.4 }}>{key}</p>
                  </div>
                ))}
                {serviziAttivi.length === 0 && (
                  <div className="cdc-detail-servizio">
                    <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>Nessun servizio registrato</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {altriServizi && altriServizi.trim() !== '' && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>Altri servizi offerti</div>
              <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0 }}>{altriServizi}</p>
            </div>
          )}

          {serviziNonAttivi.length > 0 && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginBottom: 8 }}>
                Servizi non offerti ({serviziNonAttivi.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {serviziNonAttivi.map(([key]) => (
                  <span
                    key={key}
                    title={key}
                    style={{
                      fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 999,
                      color: 'var(--text-3)', background: 'var(--surface-2, transparent)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    {FARMACIA_SHORT_LABELS[key] ?? key}
                  </span>
                ))}
              </div>
            </div>
          )}
        </BSDCard>
      </div>
    </div>
  )
}
